import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import {
  Button,
  Grid,
  styled,
  Typography,
  useMediaQuery,
  Container,
  Link,
  Alert,
} from '@mui/material'
import { differenceInYears, format, toDate } from 'date-fns'
import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { validateNRIC } from '../../data/nric-validation'
import useSnackbar from '../../data/use-snackbar'
import { getErrorMessage } from '../../error-helper'
import customerService from '../../services/customer.service'
import {
  activeStepState,
  formDataState,
  loadingState,
  isExpiredDate,
} from '../helpers'
import CheckField from '../layout/checkfield.component'
import Layout from '../layout/layout.component'
import Ekyc from './ekyc.component'
import { classes, styles } from './use-styles'
import useConfig from '../../data/use-config'
import infoLogService from '../../services/info.service'

const StyledDiv = styled('div')(styles)
const WORKPASS_TYPE_ID = 252

const getDefaultValues = (data, prefix) => {
  //fix security detect object injection; prevent to use brancket object notation with user input is present
  const remapData = _.mapKeys(data, (value, key) => {
    if (key.indexOf(prefix) > -1) return key.replace(prefix, '')
    else return key
  })

  return {
    subType: remapData['subType'],
    front: remapData['front'],
    back: remapData['back'],
    name: remapData['name'],
    idType: remapData['idType'],
    idTypeLabel: remapData['idTypeLabel'],
    nationalityLabel: remapData['nationalityLabel'],
    useEkyc: remapData['useEkyc'],
    videoIdType: remapData['videoIdType'],
    ekyc_id: remapData['ekyc_id'],
    ekyc_name: remapData['ekyc_name'],
    ekyc_idNo: remapData['ekyc_idNo'],
    ekyc_dob: remapData['ekyc_dob'],
    idNo: remapData['idNo'],
    dob: remapData['dob'] || null,
    nationality: remapData['nationality'] || '',
    contactNo: remapData['contactNo'],
    email: remapData['email'],
    address: remapData['address'],
    captureDate: remapData['captureDate'],
    singleImage: remapData['singleImage'],
    ageEligibility: remapData['ageEligibility'],
    myInfo: remapData['myInfo'],
    tamperedDocument: remapData['tamperedDocument'],
  }
}

function CustomerInfo({
  step,
  globalCheck,
  prefix,
  handleBack,
  handleCheckEligibility,
}) {
  const { data: countries } = useConfig('ctry', true)
  const setActiveStep = useSetRecoilState(activeStepState)
  const setLoading = useSetRecoilState(loadingState)
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useRecoilState(formDataState)
  const [submissionData, setSubmissionData] = useState(
    getDefaultValues(formData, prefix)
  )
  const { enqueueSnackbar } = useSnackbar()
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))

  const handleClose = (event, reason) => {
    if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
      // ignore
      return
    }

    setOpen(false)
  }

  const handleWorkpassCheckError = (type) => {
    switch (type) {
      case 'ObjectNotFound':
        return enqueueSnackbar(
          'Your document has expired.  Please re-submit with a valid document.'
        )
      case 'AccessDenied':
        return enqueueSnackbar(
          'FIN Expiry Check: AccessDenied. Please try again later'
        )
      case 'ServiceUnavailable':
        return enqueueSnackbar(
          'FIN Expiry Check: ServiceUnavailable. Please try again later'
        )
      case 'ServerError':
        return enqueueSnackbar(
          'FIN Expiry Check: ServerError. Please try again later'
        )
      case 'ServerBusy':
        return enqueueSnackbar(
          'FIN Expiry Check: ServerBusy. Please try again later'
        )
      default:
        return enqueueSnackbar(
          'FIN Expiry Check : General Error. Please try again later'
        )
    }
  }

  const handleVideoIdCompleted = async (data) => {
    if (
      data.document.expiryDateTS &&
      isExpiredDate(toDate(data.document.expiryDateTS))
    ) {
      setOpen(false)
      return enqueueSnackbar('Document is expired.')
    }

    let idNo =
      submissionData.idType === 'PASSPORT'
        ? data.document.passportNumber
        : data.document.documentNumber

    if (
      !data.document.expiryDateTS &&
      submissionData.videoIdType == WORKPASS_TYPE_ID
    ) {
      try {
        const workPassResult = await customerService.workpassCheck(
          data.document.documentNumber
        )
        if (workPassResult) {
          if (workPassResult.status == 'Ok') {
            const validStatus = workPassResult.workPass.some(
              (pass) =>
                pass.passStatus === 'Live' || pass.passStatus === 'Approved'
            )
            if (!validStatus) {
              setOpen(false)
              return handleWorkpassCheckError('ObjectNotFound')
            }
          } else {
            setOpen(false)
            return handleWorkpassCheckError(workPassResult.status)
          }
        }
      } catch (err) {
        setOpen(false)
        return handleWorkpassCheckError('FAIL')
      }
    }

    const name =
      (data.document.subject.primaryName
        ? data.document.subject.primaryName
        : '') +
      (data.document.subject.secondaryName
        ? ' ' + data.document.subject.secondaryName
        : '')

    const dob = toDate(data.document.subject.birthDateTS)

    const age = differenceInYears(Date.now(), dob)
    const ageEligibility = age >= 15 && age <= 120
    const tamperedDocument =
      data.securityChecks && data.securityChecks.untamperedDocument
        ? !data.securityChecks.untamperedDocument.passed
        : false
    const tamperedVideo =
      data.securityChecks && data.securityChecks.untamperedVideo
        ? !data.securityChecks.untamperedVideo.passed
        : false

    let address = submissionData.address

    if (submissionData.idType === 'NRIC') {
      if (
        data.document &&
        data.document.subject &&
        data.document.subject.address
      ) {
        address = data.document.subject.address.trim().replaceAll('\n', ',')
      }
    }

    try {
      infoLogService.log({
        tag: 'Re-Scan eID Result',
        info: {
          videoID: data.id,
          country: submissionData.nationality,
          eID_ID_Type: submissionData.videoIdType,
          eId_nationality: data.document.subject.nationality,
          expiredDate: data.document.expiryDateTS,
          name: name,
          idNo: idNo,
          dob: dob,
          securityChecks: JSON.stringify(data.securityChecks),
          redemptionCode: formData.redemptionCode || '',
        },
      })
    } catch (err) {
      console.log(err)
    }

    let ekycData = {
      ...submissionData,
      useEkyc: 2,
      ekyc_id: data.id,
      ekyc_name: name,
      ekyc_idNo: idNo,
      ekyc_dob: dob,
      name: name,
      idNo: idNo,
      dob: dob,
      ageEligibility: ageEligibility,
      serviceEligibility: true,
      front:
        'data:image/png;base64,' +
        (submissionData.idType === 'PASSPORT'
          ? data.document.scan
          : data.document.front),
      back:
        submissionData.idType === 'PASSPORT'
          ? null
          : 'data:image/png;base64,' + data.document.back,
      tamperedDocument: tamperedDocument,
      address: address,
    }

    if (
      submissionData.idType === 'WORKP' &&
      submissionData.nationality !== data.document.subject.nationality
    ) {
      let country = _.find(countries, {
        value1: data.document.subject.nationality,
      })
      if (country) {
        ekycData = {
          ...ekycData,
          nationality: country.value1,
          nationalityLabel: country.value2,
          nationalityBssId: country.value5,
        }
      }
    }

    if (tamperedDocument || tamperedVideo) {
      setOpen(false)
      try {
        await customerService.postDocTampered(
          ekycData.idType,
          idNo,
          name,
          dob,
          ekycData.nationality
        )
      } catch (err) {
        infoLogService.log({
          tag: 'document mismatch error',
          error: JSON.stringify(err),
        })
      }
      return enqueueSnackbar(
        <>
          We are unable to validate your document. Please refer to our{' '}
          <Link
            href="https://www.m1.com.sg/support/faq/prepaid-faq"
            target="_blank"
            rel="noopener noreferrer"
          >
            FAQs
          </Link>{' '}
          for more information.
        </>
      )
    }

    try {
      let fraudResult = await customerService.validateFraud(
        ekycData.idType,
        idNo,
        name,
        dob,
        ekycData.nationality
      )
      if (fraudResult.isBlacklisted || fraudResult.isProfanity) {
        setOpen(false)
        return enqueueSnackbar(
          'We are unable to process your registration. Please try again.'
        )
      }
    } catch (err) {
      setOpen(false)
      return enqueueSnackbar('Something went wrong. Please try again.')
    }

    setSubmissionData(ekycData)
    setOpen(false)
  }

  const handleVideoIdTimeout = useCallback(() => {
    enqueueSnackbar(
      'Unable to connect. Please check your connection and try again.'
    )
  }, [enqueueSnackbar])

  const handleVideoIdError = useCallback(() => {
    enqueueSnackbar(
      'Unable to connect. Please check your connection and try again'
    )
  }, [enqueueSnackbar])

  const handleVideoIdFailed = useCallback(
    (errorId, errorMsg, videoId) => {
      setOpen(false)
      return enqueueSnackbar(
        <Alert icon={false} severity="error">
          {errorMsg || 'Something went wrong'}. Please proceed to M1 Shop Bugis
          (11am - 8pm) for assistance if issue persists. <br />
          <br />
          <b>Error ID: </b> {errorId} <br />
          <b>Video ID: </b> {videoId} <br />
        </Alert>
      )
    },
    [enqueueSnackbar]
  )

  const handleScan = async () => {
    setOpen(true)
  }

  const handleConfirm = async () => {
    let eligibility = !globalCheck && formData.isDataOnlyPlan

    //add validation for customer info display
    if (
      _.isEmpty(submissionData.name) ||
      _.isEmpty(submissionData.idNo) ||
      _.isNull(submissionData.dob) ||
      _.isEmpty(submissionData.nationalityLabel) ||
      _.isEmpty(submissionData.nationality)
    ) {
      return enqueueSnackbar('Incomplete information. Please click "Rescan".')
    }

    // ID Number validation
    if (
      (submissionData.idType === 'NRIC' &&
        !validateNRIC(submissionData.idNo)) ||
      (submissionData.idType === 'WORKP' &&
        !validateNRIC(submissionData.idNo, false))
    ) {
      return enqueueSnackbar(
        `Invalid ${submissionData.idTypeLabel}. Please click "Rescan".`
      )
    }

    //add error message for age ineligible
    if (!submissionData.ageEligibility) {
      return enqueueSnackbar(
        'Applicants must be at least 15 years of age at time of registration.'
      )
    }

    if (!eligibility) {
      setLoading(true)
      try {
        let result = await handleCheckEligibility(
          submissionData.idType,
          submissionData.idNo,
          submissionData.nationality,
          1
        )

        if (result.valid === 'YES') {
          eligibility = true
          setLoading(false)
        } else if (result.valid === 'PENDING') {
          setLoading(false)
          enqueueSnackbar(
            'We are unable to verify your global prepaid lines limit at the moment. Please try again at a later time.'
          )
        } else if (result.valid === 'FAIL') {
          setLoading(false)
          enqueueSnackbar(
            'We are unable to verify your global prepaid lines limit at the moment. Please try again at a later time.'
          )
        } else {
          setLoading(false)
          enqueueSnackbar(
            'You have exceeded the maximum of 3 global prepaid lines. Please de-register one of your prepaid lines and try again.'
          )
        }
      } catch (error) {
        setLoading(false)
        enqueueSnackbar(getErrorMessage(error))
      }
    }

    if (eligibility) {
      handleNext(submissionData)
    }
  }

  const handleNext = (data) => {
    setFormData({
      ...formData,
      ..._.mapKeys(data, (v, key) => {
        return prefix + key
      }),
    })
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const BackConfirmBtn = () => {
    return (
      <Grid
        container
        item
        xs={12}
        md={12}
        columnSpacing={1}
        direction={{ xs: 'column-reverse', lg: 'row' }}
        sx={{ p: { xs: '0 20px' } }}
      >
        <Grid item xs={12} md={5}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={handleBack}
          >
            Back
          </Button>
        </Grid>
        <Grid item xs={12} md={5}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={false}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <Layout
        step={step}
        content={
          <StyledDiv>
            <Grid
              container
              columnSpacing={8}
              direction={{ xs: 'column-reverse', md: 'row' }}
            >
              <Grid item container xs={12} md={6}>
                {submissionData.myInfo !== 'YES' && (
                  <>
                    <Typography variant="subtitle">
                      Please ensure the information displayed is the same as the
                      ID you have just submitted.
                      <br />
                      <br />
                      If the details are incorrect, please click
                      <strong> &quot;Rescan&quot;</strong> to resubmit your
                      information.
                      <br />
                    </Typography>
                    <Container
                      disableGutters
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Button
                        endIcon={<CameraAltOutlinedIcon />}
                        variant="contained"
                        color="primary"
                        onClick={handleScan}
                      >
                        Rescan
                      </Button>
                    </Container>

                    <img
                      src={submissionData.front}
                      className={classes.doc}
                      alt="Front"
                    />
                    {submissionData && submissionData.back && (
                      <img
                        src={submissionData.back}
                        className={classes.doc}
                        alt="Back"
                      />
                    )}
                  </>
                )}
              </Grid>
              <Grid item container xs={12} md={6} height="fit-content">
                <CheckField xs={6} label="Name" value={submissionData.name} />
                <CheckField
                  xs={6}
                  label={submissionData.idTypeLabel}
                  value={submissionData.idNo}
                />
                <CheckField
                  xs={6}
                  label="Date of Birth"
                  value={format(submissionData.dob, 'dd MMM yyyy')}
                />
                <CheckField
                  xs={6}
                  label="Nationality"
                  value={submissionData.nationalityLabel}
                />
                <CheckField
                  xs={6}
                  label="Address"
                  value={submissionData.address}
                />
                {!isMobile && <BackConfirmBtn />}
              </Grid>
            </Grid>
            {isMobile && <BackConfirmBtn />}
          </StyledDiv>
        }
      />
      {submissionData && submissionData.myInfo !== 'YES' && (
        <Ekyc
          open={open}
          onClose={handleClose}
          idType={submissionData.videoIdType}
          onCompleted={handleVideoIdCompleted}
          onError={handleVideoIdError}
          onTimeout={handleVideoIdTimeout}
          onFailed={handleVideoIdFailed}
        />
      )}
    </>
  )
}

CustomerInfo.defaultProps = {
  globalCheck: true,
  prefix: '',
}

CustomerInfo.propTypes = {
  step: PropTypes.object.isRequired,
  globalCheck: PropTypes.bool,
  prefix: PropTypes.string,
  handleBack: PropTypes.func.isRequired,
  handleCheckEligibility: PropTypes.func,
}

export default CustomerInfo
